import {
  Box,
  BoxProps,
  Button,
  Card,
  CloseButton as ChakraCloseButton,
  CloseButtonProps,
  HStack,
  Heading,
  IconButton,
  Image,
  Stack,
  Text,
  VStack
} from "@chakra-ui/react"
import { isPrimaryUnmodifiedClick } from "@pathwright/utils"
import ResourceBackground from "@pathwright/ux/src/components/background/ResourceBackground"
import { DarkMode } from "@pathwright/ux/src/components/chakra/ui/color-mode"
import Link, { LinkProps } from "@pathwright/ux/src/components/link/Link"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import { AnimatePresence, motion, useInView } from "framer-motion"
import { MouseEvent, useEffect, useRef } from "react"
import { useHistory } from "react-router-dom"
import Share from "../share/Share"
import {
  OfferButton,
  offerButtonProps,
  type OfferButtonRenderProps
} from "./CoverPageOffer"
import { useCoverPageContext } from "./CoverPageProvider"
import { ANIMATION_DURATION, EASE_BEZIER } from "./animations"
import {
  DEFAULT_COVER_PAGE_CLOSE_TARGET,
  resolveCoverPageCloseTarget
} from "./coverPageCloseTarget"
import CoverPageSectionTrigger from "./design/CoverPageSectionTrigger"
import { getCoverPageDisplayImage } from "./design/utils"
import CoverPageTrailer from "./trailer/CoverPageTrailer"
import {
  useCoverPageTranslate,
  useGetPathUrl,
  useTextWrapDetection
} from "./utils"

export const COLLAPSED_HEIGHT = 70
export const IMMERSIVE_HEADER_HEIGHT = 80

const COLLAPSE_ANIMATION_DURATION = ANIMATION_DURATION.fade
const COLLAPSE_ANIMATION_EASE_ARRAY = [...EASE_BEZIER]

export const CloseButton = (props: Partial<CloseButtonProps>) => {
  const { t } = useCoverPageTranslate()
  const { closeTarget, onClose, coverRoute } = useCoverPageContext()
  const history = useHistory()
  const icon = <Pathicon icon="x-thick" />
  const destination = resolveCoverPageCloseTarget(closeTarget, coverRoute)

  const handleClose = (event: MouseEvent<HTMLAnchorElement>) => {
    if (!isPrimaryUnmodifiedClick(event)) return
    event.preventDefault()
    try {
      if (onClose) {
        onClose()
      } else {
        history.push(destination)
      }
    } catch {
      history.push(DEFAULT_COVER_PAGE_CLOSE_TARGET)
    }
  }

  return (
    <ChakraCloseButton
      position="absolute"
      size="sm"
      color="blackAlpha.600"
      top={4}
      right={4}
      p={1.5}
      h={8}
      minWidth={8}
      borderRadius="full"
      zIndex={1001}
      aria-label={t("header.close")}
      // @ts-expect-error: TODO: get typing correct using chakra typegen https://chakra-ui.com/docs/get-started/cli#chakra-typegen
      variant="blurredLightGray"
      asChild
      {...props}
    >
      <Link to={destination} onClick={handleClose}>
        {icon}
      </Link>
    </ChakraCloseButton>
  )
}

const ShareButton = () => {
  const resource = useCoverPageContext((v) => v.resource)
  const sourcePathId = resource?.curriculum_group?.id
  const { t } = useCoverPageTranslate()

  if (!sourcePathId) return null

  return (
    <Box position="absolute" top={4} right={14} zIndex={1001}>
      <Share
        context={{
          type: "cohort",
          typeId:
            typeof sourcePathId === "string"
              ? parseInt(sourcePathId, 10)
              : sourcePathId
        }}
        hideTabs
        openWithKeys
        onToggleIsOpen={null}
        placement={null}
        triggerLabel={null}
        portal={false}
        displayType="modal"
      >
        {({ renderTrigger }) =>
          renderTrigger(({ onToggle }: { onToggle: () => void }) => (
            <Button
              aria-label={t("header.share")}
              size="sm"
              color="blackAlpha.600"
              p={1}
              h={8}
              minWidth={8}
              borderRadius="full"
              zIndex={1001}
              onClick={onToggle}
              // @ts-expect-error: TODO: get typing correct using chakra typegen https://chakra-ui.com/docs/get-started/cli#chakra-typegen
              variant="blurredLightGray"
            >
              <Pathicon icon="share-fill" size="1.1rem" />
            </Button>
          ))
        }
      </Share>
    </Box>
  )
}

export const ResourceImageCollapsed = ({
  isActive,
  fadeInDuration,
  fadeOutDuration,
  fadeInDelay,
  fadeOutDelay,
  layoutId,
  ...linkProps
}: {
  isActive: boolean
  fadeInDuration?: number
  fadeOutDuration?: number
  fadeInDelay?: number
  fadeOutDelay?: number
  layoutId?: string
} & LinkProps) => {
  const { t } = useCoverPageTranslate()
  const paintResource = useCoverPageContext((v) => v.paintResource)
  const { image_raw, cover_image, cover_display_image } = paintResource || {}
  const displayImage = getCoverPageDisplayImage({
    cover_display_image,
    cover_image,
    image_raw
  })

  return (
    <Link asChild {...linkProps}>
      <Card.Root
        asChild
        borderRadius="10px"
        maxWidth="80px"
        w="80px"
        cursor="pointer"
        alignSelf="center"
        justifySelf="center"
        shadow="card"
        overflow="hidden"
        pos="relative"
        border="none"
      >
        <motion.a
          layoutId={layoutId}
          initial={{ opacity: 0 }}
          animate={{
            opacity: isActive ? 1 : 0,
            display: "block",
            transition: {
              duration: isActive
                ? fadeInDuration ?? COLLAPSE_ANIMATION_DURATION
                : fadeOutDuration ?? 0,
              delay: isActive ? fadeInDelay ?? 0 : fadeOutDelay ?? 0,
              ease: COLLAPSE_ANIMATION_EASE_ARRAY
            },
            transitionEnd: { display: isActive ? "block" : "none" }
          }}
          exit={{ opacity: 0 }}
        >
          <Image
            src={displayImage}
            alt={t("header.cover_image_alt")}
            maxW="100%"
            w="auto"
            h="auto"
            maxH="100%"
            objectFit="cover"
          />
        </motion.a>
      </Card.Root>
    </Link>
  )
}

export const PricingButton = ({
  collapsed,
  renderButton,
  ...buttonProps
}: {
  collapsed: boolean
  renderButton?: (props: OfferButtonRenderProps) => React.ReactNode
} & OfferButtonRenderProps & {
    leftIcon?: React.ReactNode
    rightIcon?: React.ReactNode
  }) => {
  const { t } = useCoverPageTranslate()
  const { offers, offersLoading, offersSectionRef } = useCoverPageContext()

  const loading = offersLoading
  const empty = !loading && !offers?.length

  const isSingleOfferOption =
    offers?.length === 1 && offers?.[0]?.price_options?.length === 1

  const isMultipleOfferOptions = offers?.length && !isSingleOfferOption

  const buttonLabel = isSingleOfferOption
    ? offers?.[0]?.price_options?.[0]?.button_label
    : t("join")
  const description = isSingleOfferOption
    ? offers?.[0]?.price_options?.[0]?.price_description
    : t("header.options_count", { count: offers?.length || 0 })

  const handleClick = () => {
    if (!offersSectionRef?.current) return
    offersSectionRef.current.scrollIntoView({
      behavior: "smooth",
      block: "start"
    })
  }

  const children = (
    <>
      {buttonProps.leftIcon}
      {buttonProps?.children || buttonLabel}
      {buttonProps.rightIcon}
    </>
  )

  const headerOfferButtonProps: OfferButtonRenderProps = {
    ...offerButtonProps,
    w: "100%",
    colorPalette: "brand",
    onClick: isMultipleOfferOptions ? handleClick : undefined,
    cursor: !loading ? "pointer" : "default",
    disabled: loading,
    loading: loading,
    ...(buttonProps || {}),
    children
  }

  const mainButton = isSingleOfferOption ? (
    <OfferButton
      offer={offers?.[0]}
      selectedOptionIndex={0}
      buttonProps={headerOfferButtonProps}
      renderButton={renderButton}
    />
  ) : renderButton ? (
    renderButton(headerOfferButtonProps)
  ) : (
    <Button {...headerOfferButtonProps}>{children}</Button>
  )

  if (empty) return null

  return (
    <Stack
      minW={collapsed ? "0" : "200px"}
      flexDirection={collapsed ? "row" : "column"}
      gap="5px"
      align="flex-start"
    >
      <DarkMode>{mainButton}</DarkMode>
      {!loading && description && !collapsed ? (
        <Box
          m={0}
          mb="5px"
          lineClamp={1}
          w="100%"
          fontSize="xs"
          color="white"
          textAlign="center"
          lineHeight="1 !important"
          dangerouslySetInnerHTML={{
            __html: description
          }}
        />
      ) : null}
    </Stack>
  )
}

const HeaderVideo = () => {
  const { t } = useCoverPageTranslate()
  const {
    videoFocused,
    resource,
    backgroundVideoMuted,
    setShowVideoMuteButton,
    setBackgroundVideoMuted
  } = useCoverPageContext()

  const trailerEmbedCode = resource?.trailer_embed_code

  if (!trailerEmbedCode) return null

  // Background mode: when there's a trailer and we're not in focused mode
  const shouldBeInBackgroundMode = !!trailerEmbedCode && !videoFocused

  return (
    <>
      <HStack
        position="absolute"
        top={{ base: 2, md: 4 }}
        right={{ base: 2, md: 4 }}
        gap={2}
        zIndex={videoFocused ? 101 : 12}
      >
        <IconButton
          aria-label={t("header.mute_video")}
          onClick={() => setBackgroundVideoMuted(!backgroundVideoMuted)}
          lineHeight="1rem"
          h="24px"
          w="24px"
          // @ts-expect-error: TODO: get typing correct using chakra typegen https://chakra-ui.com/docs/get-started/cli#chakra-typegen
          variant="blurredLightGray"
          // colorPalette="whiteAlpha"
          children={
            <Pathicon
              icon={backgroundVideoMuted ? "audio-off" : "audio"}
              size={"18px"}
            />
          }
          p={2}
          minW="0"
          borderRadius="full"
          cursor="pointer"
        />
      </HStack>
      <CoverPageTrailer
        trailerEmbedCode={trailerEmbedCode}
        backgroundMode={shouldBeInBackgroundMode}
        // When focused (not background), start unmuted regardless of previous background mute state
        muted={shouldBeInBackgroundMode ? backgroundVideoMuted : false}
        onParsePlatform={(service) => {
          const show = service === "youtube" || service === "vimeo"
          setShowVideoMuteButton?.(show)
        }}
        containerStyles={{
          width: "100%",
          height: "100%",
          maxWidth: "1000px"
        }}
      />
    </>
  )
}

const Layer = ({
  children,
  zIndex,
  ...props
}: {
  children?: React.ReactNode
  zIndex: number
} & BoxProps) => {
  return (
    <Box
      position="absolute"
      top="0"
      left="0"
      right="0"
      bottom="0"
      zIndex={zIndex}
      {...props}
    >
      {children || null}
    </Box>
  )
}

const ExpandedHeader = ({ isCollapsed }: { isCollapsed: boolean }) => {
  const { t } = useCoverPageTranslate()
  const {
    resource,
    contributors,
    videoFocused,
    contributorSectionRef,
    categories,
    moreSectionRef,
    isMobile,
    moreResources,
    licensorSchoolId
  } = useCoverPageContext()
  const paintResource = useCoverPageContext((v) => v.paintResource)
  const { short_description, name } = paintResource || {}
  const displayImage = getCoverPageDisplayImage(paintResource)
  const is_ambient_background = paintResource?.is_ambient_background
  const trailer_embed_code = resource?.trailer_embed_code

  // For now, only link to path if original to school (not a licensor).
  const pathUrl = !licensorSchoolId && useGetPathUrl()

  // We support 2 Title layouts: unwrapped (image to left of title; image is larger)
  // and wrapped (image to top of title; image is smaller)
  const { headingRef, imageRef, titleRowRef } = useTextWrapDetection(
    name || "",
    [name || ""]
  )
  // Track previous collapse state to alter transition direction
  const prevCollapsedRef = useRef(isCollapsed)
  const wasCollapsed = prevCollapsedRef.current
  if (wasCollapsed !== isCollapsed) prevCollapsedRef.current = isCollapsed
  const isBecomingCollapsed = !wasCollapsed && isCollapsed

  const showMoreResources = moreResources?.length && categories?.length

  const heading = (
    <Box
      ref={titleRowRef}
      as="div"
      display={{ base: "flex", md: "block" }}
      flexDirection={{ base: "column", md: "row" }}
      alignItems={{ base: "center", md: "start" }}
      zIndex={4}
      w={{ base: "95%", md: "100%" }}
      maxW={{ base: "400px", md: "100%" }}
      mx="auto"
      position="relative"
      _hover={{
        "& .CoverPageHeader__heading-link": {
          textDecoration: "underline",
          textDecorationThickness: "2px",
          textUnderlineOffset: "6px",
          textDecorationColor: "rgba(255, 255, 255, 0.6)"
        }
      }}
    >
      <Box
        asChild
        display="inline"
        pos={{ base: undefined, md: "absolute" }}
        top="-6px"
        lineHeight="1.15"
      >
        <motion.div layoutId="cover-thumb">
          <Image
            ref={imageRef as any}
            src={displayImage}
            alt={t("header.cover_image_alt", { defaultValue: "Cover Image" })}
            display="block"
            float={{ base: "none", md: "left" }}
            mr={{ base: 0, md: "10px" }}
            mb={{ base: "8px", md: 0 }}
            w="80px"
            minW="80px"
            h="auto"
            objectFit="contain"
            borderRadius="10px"
            boxShadow="0 2px 8px 0 rgba(0, 0, 0, 0.2)"
          />
        </motion.div>
      </Box>
      <Heading
        asChild
        ref={headingRef}
        display="block"
        as="h2"
        textIndent={{ base: undefined, md: "90px" }}
        lineHeight="1.15"
        fontSize={{ base: "24px", md: "48px" }}
        cursor={pathUrl ? "pointer" : "default"}
        fontWeight="700"
        textShadow="0 4px 4px rgba(0, 0, 0, 0.15)"
        className="theme-heading theme-heading-bold"
        m={0}
        mr={0}
        color="white"
        textAlign={{ base: "center", md: "left" }}
        width="auto"
      >
        <motion.h2>{name}</motion.h2>
      </Heading>
    </Box>
  )

  return (
    <VStack
      as="header"
      w="100%"
      px={{ base: 4, sm: 8, md: "50px" }}
      pt={{ base: "30px", md: "50px" }}
      pb={{ base: 4, md: "30px" }}
      gap={{ base: "0.5rem", md: "50px" }}
      justifyContent="space-between"
      alignItems={{ base: "center", md: "start" }}
      zIndex={1}
      position="relative"
      id="header"
      role="group"
      aspectRatio={
        !is_ambient_background || trailer_embed_code ? "16/9" : "auto"
      }
    >
      <CoverPageSectionTrigger sectionKey="header" top={4} right={24} />
      <ShareButton />
      <CloseButton />
      {trailer_embed_code ? (
        <Layer zIndex={videoFocused ? 100 : 1}>
          <HeaderVideo />
        </Layer>
      ) : (
        <Layer zIndex={is_ambient_background ? 2 : 1}>
          <ResourceBackground
            resource={paintResource}
            position="center"
            forceBlur={!!is_ambient_background}
            style={{
              filter: isCollapsed ? "blur(15px)" : "none",
              transition: `filter ${
                isBecomingCollapsed ? COLLAPSE_ANIMATION_DURATION : 0
              }s cubic-bezier(${EASE_BEZIER.join(", ")})`
            }}
          />
        </Layer>
      )}
      {/* Background gradients to help text stand out better */}
      {(() => {
        const isAmbient = !!is_ambient_background
        const bottomGradient =
          isAmbient || !displayImage
            ? undefined
            : `linear-gradient(to top, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0) 50%)`
        const radialVignette =
          "radial-gradient(50% 50% at 50% 50%, rgba(0, 0, 0, 0.00) 74.52%, rgba(0, 0, 0, 0.10) 100%)"

        // Use fixed pixel heights (responsive) instead of percentages
        const bottomHeights = isAmbient
          ? { base: "260px", sm: "300px", lg: "340px" }
          : { base: "300px", sm: "340px", lg: "380px" }

        return (
          <>
            {/* Radial vignette primarily for video; subtle otherwise */}
            <Layer
              bg={radialVignette}
              zIndex={3}
              className="radial-vignette"
              pointerEvents="none"
            />
            {/* Bottom fade for text/buttons */}
            <Layer
              bg={bottomGradient}
              className="bottom-gradient"
              pointerEvents="none"
              zIndex={3}
              top={"auto"}
              height={bottomHeights}
            />
          </>
        )
      })()}
      <>
        <Link
          to={pathUrl || "#"}
          display="block"
          w="full"
          tabIndex={pathUrl ? undefined : -1}
          cursor={pathUrl ? "pointer" : "default"}
          pointerEvents={pathUrl ? "auto" : "none"}
          textDecoration="none"
          _hover={
            pathUrl
              ? {
                  "& h2": {
                    textDecoration: "underline",
                    textDecorationThickness: "2px",
                    textUnderlineOffset: "6px",
                    textDecorationColor: "rgba(255, 255, 255, 0.6)"
                  }
                }
              : undefined
          }
        >
          {heading}
        </Link>
        <Stack
          alignItems={{ base: "center", md: "start" }}
          gap="15px"
          zIndex={4}
          w={{ base: "95%", md: "100%" }}
          maxW={{ base: "400px", md: "100%" }}
          direction={{ base: "column-reverse", lg: "row" }}
        >
          <Box asChild>
            <motion.div layoutId="pricing-expanded">
              <PricingButton collapsed={false} />
            </motion.div>
          </Box>
          <VStack alignItems="start" gap={"5px"}>
            {short_description ? (
              <Text
                m="0"
                my={{ base: "0.5rem", md: "0" }}
                textAlign={{ base: "center", md: "left" }}
                color="white"
                // textShadow="0 1px 4px rgba(0, 0, 0, 0.15)"
              >
                {short_description}
              </Text>
            ) : null}
            <Stack
              gap={"0.5rem"}
              w={{ base: "100%", md: "auto" }}
              alignItems="center"
              direction={{ base: "column", md: "row" }}
            >
              {trailer_embed_code ? (
                <DarkMode>
                  <Link
                    relativeTo="trailer"
                    variant="underline"
                    textAlign={{ base: "center", md: "left" }}
                  >
                    <Pathicon icon="play" />
                    {t("header.play_trailer")}
                  </Link>
                </DarkMode>
              ) : null}
              {trailer_embed_code && contributors?.length && !isMobile ? (
                <Box
                  lineHeight="1"
                  as="span"
                  color="white"
                  textAlign={{ base: "center", md: "left" }}
                  fontSize="xs"
                  m={0}
                >
                  &bull;
                </Box>
              ) : null}
              {contributors?.length ? (
                <Text
                  lineHeight="1"
                  color="white"
                  fontSize="xs"
                  textAlign={{ base: "center", md: "left" }}
                  m={0}
                  _hover={{
                    color: "rgba(255, 255, 255, 0.8)"
                  }}
                  fontWeight={{ base: "700", md: "400" }}
                  onClick={() => {
                    contributorSectionRef.current?.scrollIntoView({
                      behavior: "smooth",
                      block: "start"
                    })
                  }}
                  cursor="pointer"
                >
                  {/* Replace spaces with non-breaking spaces to ensure full names stay together */}
                  {t("header.from")}{" "}
                  {contributors
                    .map((a, i) =>
                      (a.display_name || "").replace(/ /g, "\u00A0")
                    )
                    .join(", ")}
                </Text>
              ) : null}
              {(trailer_embed_code || contributors?.length) &&
              showMoreResources &&
              !isMobile ? (
                <Box lineHeight="1" as="span" color="white" fontSize="xs" m={0}>
                  &bull;
                </Box>
              ) : null}
              {showMoreResources ? (
                <Text
                  color="white"
                  textStyle="xs"
                  m={0}
                  onClick={() =>
                    moreSectionRef.current?.scrollIntoView({
                      behavior: "smooth",
                      block: "start"
                    })
                  }
                  textAlign={{ base: "center", md: "left" }}
                  fontWeight={{ base: "700", md: "400" }}
                  cursor="pointer"
                  _hover={{
                    color: "rgba(255, 255, 255, 0.8)"
                  }}
                >
                  {(() => {
                    const first = categories[0]?.name
                    const extra = categories.length - 1
                    return extra > 0
                      ? t("header.category_more", {
                          first,
                          count: extra
                        })
                      : first
                  })()}
                </Text>
              ) : null}
            </Stack>
          </VStack>
        </Stack>
      </>
    </VStack>
  )
}

const CollapsedHeader = ({ isActive }: { isActive: boolean }) => {
  const paintResource = useCoverPageContext((v) => v.paintResource)
  const isMobile = useCoverPageContext((v) => v.isMobile)
  const loadingNextCoverPage = useCoverPageContext(
    (v) => v.loadingNextCoverPage
  )
  const queriesAreLoading = useCoverPageContext((v) => v.queriesAreLoading)
  const scrollToHeaderTrigger = useCoverPageContext((v) =>
    v.scrollToSection ? () => v.scrollToSection("header") : undefined
  )

  const { name } = paintResource || {}
  const is_ambient_background = paintResource?.is_ambient_background

  // Track the previous isActive state to determine animation direction
  const prevIsActiveRef = useRef(isActive)
  const isExpandingRef = useRef(false)
  const isCollapsingRef = useRef(false)

  // Update refs when isActive changes
  if (prevIsActiveRef.current !== isActive) {
    isExpandingRef.current =
      prevIsActiveRef.current === true && isActive === false
    isCollapsingRef.current =
      prevIsActiveRef.current === false && isActive === true
    prevIsActiveRef.current = isActive
  }

  const isExpanding = isExpandingRef.current

  return (
    <Box
      width="100%"
      display="flex"
      alignItems="center"
      flexDirection={"row"}
      gap={{ base: 2, md: 5 }}
      maxH={isActive ? { base: "auto", md: `${COLLAPSED_HEIGHT}px` } : 0}
      as="header"
      className="CollapsedHeader"
      mt={isActive ? `-${COLLAPSED_HEIGHT}px` : 0}
      py={isActive ? 2 : 0}
      pr={isActive ? 4 : 0}
      pl={isActive ? 8 : 0}
      position="sticky"
      top={0}
      w="100%"
      zIndex={
        isActive && !loadingNextCoverPage && !queriesAreLoading ? 111 : -1
      }
    >
      {/* <CloseButton top="50%" transform="translateY(-50%)" /> */}
      <ResourceBackground
        resource={paintResource}
        position="center"
        forceBlur={!!is_ambient_background}
        style={{
          position: "absolute",
          width: "100%"
        }}
      />
      <AnimatePresence initial={false}>
        {isActive ? (
          <ResourceImageCollapsed
            key="collapsed-thumb"
            onClick={scrollToHeaderTrigger}
            isActive={true}
            fadeInDelay={0}
            fadeOutDuration={0}
            fadeOutDelay={0}
          />
        ) : null}
      </AnimatePresence>
      <Stack
        flexDirection={"row"}
        justifyContent={{ base: "flex-start", md: "space-between" }}
        alignItems={"center"}
        w="100%"
        gap={2}
        zIndex={1}
      >
        <AnimatePresence initial={false}>
          {isActive ? (
            <Heading
              asChild
              key="collapsed-title"
              as="h2"
              fontSize={["19px", "22px"]}
              // leave space on right for close button
              pr={{ base: "40px", md: 0 }}
              m={0}
              color="white"
              fontWeight="700"
              textShadow="0 1px 5px rgba(0, 0, 0, 0.10)"
              textAlign={"left"}
              cursor="pointer"
              onClick={scrollToHeaderTrigger}
            >
              <motion.h2
                initial={{ opacity: 0 }}
                animate={{
                  opacity: 1,
                  display: "block",
                  transition: {
                    duration: COLLAPSE_ANIMATION_DURATION,
                    delay: 0.05,
                    ease: COLLAPSE_ANIMATION_EASE_ARRAY
                  },
                  transitionEnd: { display: "block" }
                }}
                exit={{ opacity: 0, transition: { duration: 0 } }}
              >
                {name}
              </motion.h2>
            </Heading>
          ) : null}
        </AnimatePresence>
        {!isExpanding && !isMobile ? (
          <Stack asChild>
            <motion.div
              layoutId="pricing-collapsed"
              initial={{ opacity: 0 }}
              animate={{
                opacity: isActive ? 1 : 0,
                display: "block",
                transition: {
                  duration: isActive ? COLLAPSE_ANIMATION_DURATION : 0,
                  delay: isActive ? 0.1 : 0,
                  ease: COLLAPSE_ANIMATION_EASE_ARRAY
                },
                transitionEnd: { display: isActive ? "block" : "none" }
              }}
            >
              <PricingButton collapsed />
            </motion.div>
          </Stack>
        ) : null}
      </Stack>
    </Box>
  )
}

const CoverPageHeaderContainer = () => {
  const headerTriggerRef = useCoverPageContext((v) => v.headerTriggerRef)
  const scrollContainerRef = useCoverPageContext((v) => v.scrollContainerRef)
  const headerCollapsed = useCoverPageContext((v) => v.headerCollapsed)
  const setHeaderCollapsed = useCoverPageContext((v) => v.setHeaderCollapsed)
  const resourceId = useCoverPageContext((v) => v.resourceId)

  const inView = useInView(headerTriggerRef, {
    root:
      (scrollContainerRef as unknown as { current: Element | null }) ||
      undefined,
    margin: `-10px 0px 0px 0px`
  })

  // useInView starts false, before it has measured. That is not "scrolled
  // away" — collapsed is only reachable after this resource's trigger has
  // been on screen and the user then scrolls it off.
  const seenInViewResourceIdRef = useRef<number | null>(null)
  if (inView) seenInViewResourceIdRef.current = resourceId
  const isCollapsed = seenInViewResourceIdRef.current === resourceId && !inView

  useEffect(() => {
    if (headerCollapsed !== isCollapsed) {
      setHeaderCollapsed(isCollapsed)
    }
  }, [headerCollapsed, isCollapsed, setHeaderCollapsed])

  return (
    <>
      <ExpandedHeader isCollapsed={isCollapsed} />
      <Box
        ref={headerTriggerRef}
        className="trigger"
        h="1px"
        mt={`-${COLLAPSED_HEIGHT}px`}
        mb={`60px`}
      />
      <CollapsedHeader isActive={isCollapsed} />
    </>
  )
}

export default CoverPageHeaderContainer
